import React, { useState, useEffect } from "react";
import {
  useLazyGetMediaQuery,
  useGetMediaFoldersQuery,
  useUpdateMediaMutation,
  useDeleteMediaMutation,
} from "../api/businessApi";
import MediaUploader from "./MediaUploader";
import Swal from "sweetalert2";

const MediaLibrary = () => {
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [search, setSearch] = useState("");
  const [folder, setFolder] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [viewMode, setViewMode] = useState("grid"); // grid or list
  const [editingMedia, setEditingMedia] = useState(null);
  const [editForm, setEditForm] = useState({ alt_text: "", folder: "" });

  const [getMedia, { data, isLoading, isFetching }] = useLazyGetMediaQuery();
  const { data: folders = [] } = useGetMediaFoldersQuery();
  const [updateMedia] = useUpdateMediaMutation();
  const [deleteMedia] = useDeleteMediaMutation();

  useEffect(() => {
    getMedia({ page, limit, search, folder, mime_type: typeFilter });
  }, [page, limit, search, folder, typeFilter, getMedia]);

  const media = data?.media || [];
  const pagination = data?.pagination || {};

  const handleUploadComplete = () => {
    getMedia({ page: 1, limit, search, folder, mime_type: typeFilter });
    setPage(1);
  };

  const handleDelete = async (id, filename) => {
    const result = await Swal.fire({
      title: "Delete File?",
      text: `Are you sure you want to delete "${filename}"? This cannot be undone.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc2626",
      confirmButtonText: "Delete",
    });

    if (result.isConfirmed) {
      try {
        await deleteMedia(id).unwrap();
        Swal.fire("Deleted!", "File has been deleted.", "success");
        if (selectedMedia?.id === id) setSelectedMedia(null);
      } catch (error) {
        Swal.fire("Error", "Failed to delete file.", "error");
      }
    }
  };

  const handleEdit = (item) => {
    setEditingMedia(item);
    setEditForm({ alt_text: item.alt_text || "", folder: item.folder || "" });
  };

  const handleSaveEdit = async () => {
    try {
      await updateMedia({ id: editingMedia.id, ...editForm }).unwrap();
      setEditingMedia(null);
      Swal.fire("Updated!", "File metadata updated.", "success");
    } catch (error) {
      Swal.fire("Error", "Failed to update file.", "error");
    }
  };

  const copyUrl = (url) => {
    navigator.clipboard.writeText(url);
    Swal.fire({ title: "Copied!", text: "URL copied to clipboard", icon: "success", timer: 1500, showConfirmButton: false });
  };

  const isImage = (mimeType) => mimeType?.startsWith("image/");
  const formatSize = (bytes) => {
    if (!bytes) return "—";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div>
      {/* Upload Section */}
      <div className="mb-6">
        <MediaUploader onUploadComplete={handleUploadComplete} />
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3 mb-6">
        {/* Search */}
        <div className="relative flex-1 min-w-[120px] sm:min-w-[200px]">
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="text"
            placeholder="Search files..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
          />
          {search && (
            <button onClick={() => { setSearch(""); setPage(1); }} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>

        {/* Type Filter */}
        <select
          value={typeFilter}
          onChange={(e) => { setTypeFilter(e.target.value); setPage(1); }}
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
        >
          <option value="">All types</option>
          <option value="image">Images</option>
          <option value="video">Videos</option>
          <option value="application">Documents</option>
        </select>

        {/* Folder Filter */}
        {folders.length > 0 && (
          <select
            value={folder}
            onChange={(e) => { setFolder(e.target.value); setPage(1); }}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
          >
            <option value="">All folders</option>
            {folders.map((f) => (
              <option key={f} value={f}>{f}</option>
            ))}
          </select>
        )}

        {/* View Toggle */}
        <div className="flex border border-gray-300 rounded-lg overflow-hidden">
          <button
            onClick={() => setViewMode("grid")}
            className={`p-2 ${viewMode === "grid" ? "bg-indigo-600 text-white" : "bg-white text-gray-600 hover:bg-gray-50"}`}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zm10 0a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zm10 0a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
            </svg>
          </button>
          <button
            onClick={() => setViewMode("list")}
            className={`p-2 ${viewMode === "list" ? "bg-indigo-600 text-white" : "bg-white text-gray-600 hover:bg-gray-50"}`}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 10h16M4 14h16M4 18h16" />
            </svg>
          </button>
        </div>

        {/* Count */}
        <span className="text-sm text-gray-500">
          {pagination.total || 0} files
        </span>
      </div>

      {/* Loading */}
      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <div className="w-10 h-10 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
        </div>
      ) : media.length === 0 ? (
        <div className="text-center py-16 text-gray-500">
          <svg className="w-20 h-20 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          <p className="text-lg font-medium">No media files yet</p>
          <p className="text-sm mt-1">Upload files using the area above</p>
        </div>
      ) : viewMode === "grid" ? (
        /* Grid View */
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
          {media.map((item) => (
            <div
              key={item.id}
              className={`group relative rounded-lg overflow-hidden border-2 transition-all duration-150 cursor-pointer ${
                selectedMedia?.id === item.id
                  ? "border-indigo-600 ring-2 ring-indigo-200 shadow-lg"
                  : "border-gray-200 hover:border-gray-300 hover:shadow-md"
              }`}
              onClick={() => setSelectedMedia(selectedMedia?.id === item.id ? null : item)}
            >
              <div className="aspect-square bg-gray-100 flex items-center justify-center">
                {isImage(item.mime_type) ? (
                  <img
                    src={item.file_url}
                    alt={item.alt_text || item.filename}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="flex flex-col items-center p-3">
                    <svg className="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                    </svg>
                    <span className="text-xs text-gray-500 mt-2 truncate w-full text-center">
                      {item.mime_type?.split("/")[1]?.toUpperCase()}
                    </span>
                  </div>
                )}
              </div>
              <div className="p-2 bg-white">
                <p className="text-xs text-gray-700 truncate font-medium">{item.filename}</p>
                <p className="text-xs text-gray-400">{formatSize(item.size)}</p>
              </div>
              {/* Hover actions */}
              <div className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity flex space-x-1">
                <button
                  onClick={(e) => { e.stopPropagation(); copyUrl(item.file_url); }}
                  className="w-7 h-7 bg-white/90 rounded-full flex items-center justify-center shadow hover:bg-white"
                  title="Copy URL"
                >
                  <svg className="w-3.5 h-3.5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                  </svg>
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); handleDelete(item.id, item.filename); }}
                  className="w-7 h-7 bg-red-500/90 rounded-full flex items-center justify-center shadow hover:bg-red-600"
                  title="Delete"
                >
                  <svg className="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        /* List View */
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Preview</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Filename</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Size</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Folder</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Uploaded</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {media.map((item) => (
                <tr key={item.id} className="hover:bg-gray-50">
                  <td className="px-4 py-2">
                    <div className="w-10 h-10 bg-gray-100 rounded overflow-hidden flex items-center justify-center">
                      {isImage(item.mime_type) ? (
                        <img src={item.file_url} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                        </svg>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-2 text-sm text-gray-900 max-w-[200px] truncate">{item.filename}</td>
                  <td className="px-4 py-2 text-sm text-gray-500">{item.mime_type?.split("/")[1]}</td>
                  <td className="px-4 py-2 text-sm text-gray-500">{formatSize(item.size)}</td>
                  <td className="px-4 py-2 text-sm text-gray-500">{item.folder || "—"}</td>
                  <td className="px-4 py-2 text-sm text-gray-500">
                    {new Date(item.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-2 text-right">
                    <div className="flex items-center justify-end space-x-2">
                      <button
                        onClick={() => copyUrl(item.file_url)}
                        className="text-indigo-600 hover:text-indigo-800 text-sm"
                        title="Copy URL"
                      >
                        Copy URL
                      </button>
                      <button
                        onClick={() => handleEdit(item)}
                        className="text-gray-600 hover:text-gray-800 text-sm"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(item.id, item.filename)}
                        className="text-red-600 hover:text-red-800 text-sm"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Selected Media Detail Panel */}
      {selectedMedia && viewMode === "grid" && (
        <div className="mt-6 bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
          <div className="flex flex-col sm:flex-row items-start gap-6">
            <div className="w-full sm:w-48 h-48 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0">
              {isImage(selectedMedia.mime_type) ? (
                <img src={selectedMedia.file_url} alt={selectedMedia.alt_text} className="w-full h-full object-contain" />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <svg className="w-16 h-16 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>
                </div>
              )}
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-gray-900 text-lg mb-3">{selectedMedia.filename}</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2 text-sm">
                <div><span className="text-gray-500">Type:</span> <span className="text-gray-900">{selectedMedia.mime_type}</span></div>
                <div><span className="text-gray-500">Size:</span> <span className="text-gray-900">{formatSize(selectedMedia.size)}</span></div>
                <div><span className="text-gray-500">Folder:</span> <span className="text-gray-900">{selectedMedia.folder || "None"}</span></div>
                <div><span className="text-gray-500">Alt text:</span> <span className="text-gray-900">{selectedMedia.alt_text || "None"}</span></div>
                <div><span className="text-gray-500">Uploaded by:</span> <span className="text-gray-900">{selectedMedia.uploaded_by || "—"}</span></div>
                <div><span className="text-gray-500">Date:</span> <span className="text-gray-900">{new Date(selectedMedia.created_at).toLocaleString()}</span></div>
              </div>
              <div className="mt-4 flex space-x-3">
                <button onClick={() => copyUrl(selectedMedia.file_url)} className="px-3 py-1.5 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition">
                  Copy URL
                </button>
                <button onClick={() => handleEdit(selectedMedia)} className="px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition">
                  Edit Details
                </button>
                <button onClick={() => handleDelete(selectedMedia.id, selectedMedia.filename)} className="px-3 py-1.5 text-sm bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition">
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div className="flex items-center justify-between mt-6">
          <p className="text-sm text-gray-500">
            Showing {(page - 1) * limit + 1}-{Math.min(page * limit, pagination.total)} of {pagination.total}
          </p>
          <div className="flex space-x-2">
            <button
              onClick={() => setPage(Math.max(1, page - 1))}
              disabled={page <= 1}
              className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg disabled:opacity-50 hover:bg-gray-50 transition"
            >
              Previous
            </button>
            <button
              onClick={() => setPage(Math.min(pagination.totalPages, page + 1))}
              disabled={page >= pagination.totalPages}
              className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg disabled:opacity-50 hover:bg-gray-50 transition"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editingMedia && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50" onClick={() => setEditingMedia(null)} />
          <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-md p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Edit Media Details</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Alt Text</label>
                <input
                  type="text"
                  value={editForm.alt_text}
                  onChange={(e) => setEditForm({ ...editForm, alt_text: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  placeholder="Describe this image for accessibility"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Folder</label>
                <input
                  type="text"
                  value={editForm.folder}
                  onChange={(e) => setEditForm({ ...editForm, folder: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  placeholder="e.g. events, logos, blog"
                />
              </div>
            </div>
            <div className="flex justify-end space-x-3 mt-6">
              <button onClick={() => setEditingMedia(null)} className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition">
                Cancel
              </button>
              <button onClick={handleSaveEdit} className="px-4 py-2 text-sm text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 transition">
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MediaLibrary;
